import { Badge, Button, useToggle } from '@commenda/kakashi';
import {
  GovernanceRole,
  IPersonOfBusinessEntity,
  KYCType,
  PersonLocationType,
} from '@commenda/types';
import {
  cn,
  copyToClipboard,
  getPrintableBusinessLocation,
} from '@commenda/utils';
import { Country as CountryInfo } from 'country-state-city';
import { useRouter } from 'next/router';
import { type ReactNode } from 'react';
import { useNotification } from '../../hooks/useNotification';
import { DataPoint } from '../common/DataPoint';
import { AddKeyPersonModal } from './Modals/AddKeyPersonModal';
import { DeleteKeyPersonModal } from './Modals/DeleteKeyPersonModal';
import {
  getKeyPersonRoles,
  KEY_PERSON_ROLES_ENUM_TO_TEXT,
  KeyPersonRolesEnum,
} from './utils';

const KeyPersonDetails = ({
  person,
  id,
  companyId,
  keyPersonId,
  isEditable,
  disabled = false,
  className,
  renderHeader,
  deleteRedirectUrl,
}: {
  person: IPersonOfBusinessEntity;
  id: string;
  companyId: number;
  keyPersonId: number;
  isEditable: boolean;
  disabled?: boolean;
  className?: string;
  /** When provided, replaces the default heading + action buttons row. Receives action buttons as a ReactNode. */
  renderHeader?: (actions: ReactNode) => ReactNode;
  /** When provided, overrides the default redirect URL after a successful deletion or edit. */
  deleteRedirectUrl?: string;
}) => {
  const router = useRouter();
  const { setNotification } = useNotification();

  const addKeyPersonModalVisibility = useToggle();
  const deleteKeyPersonModalVisibility = useToggle();

  const keyPersonAddress = person.locations?.find((location) =>
    location.type.includes(PersonLocationType.OPERATING)
  )?.address;

  const keyPersonName = [person.firstName, person.lastName]
    .filter(Boolean)
    .join(' ');

  const isAuthorizedSignatory =
    !!person.governanceRelationships?.[GovernanceRole.AUTHORIZED_SIGNATORY]
      ?.isAuthorizedSignatory;

  const isDirector =
    person.governanceRelationships?.[GovernanceRole.DIRECTOR]?.isDirector;

  const roles = getKeyPersonRoles(person);

  const rolesString = isDirector
    ? [
      'Director',
      ...roles
        .filter((role) => role !== KeyPersonRolesEnum.DIRECTOR)
        .map((role) => KEY_PERSON_ROLES_ENUM_TO_TEXT[role] || role),
    ].join(', ')
    : roles
      .map((role) => KEY_PERSON_ROLES_ENUM_TO_TEXT[role] || role)
      .join(', ');

  const designationString = person.jobTitle?.join(', ') || 'N/A';

  const ssnKyc = person.kycIds?.find((kyc) => kyc.type === KYCType.US_SSN);

  const onCopy = (value: string) => {
    const success = copyToClipboard(value);
    setNotification({
      show: true,
      valid: success,
      title: success ? 'Success' : 'Error',
      message: success ? 'Copied to clipboard' : 'Failed to copy to clipboard',
    });
  };

  return (
    <div
      className={cn(
        'flex-1 h-full overflow-y-auto pb-8xl flex flex-col gap-4xl',
        className
      )}
    >
      <AddKeyPersonModal
        isOpen={addKeyPersonModalVisibility.visible}
        onClose={addKeyPersonModalVisibility.hide}
        businessEntityId={+id}
        companyId={+companyId}
        onSuccess={() => {
          addKeyPersonModalVisibility.hide();
          router.replace(router.asPath);
        }}
        isEditMode
        personId={+keyPersonId}
      />
      <DeleteKeyPersonModal
        isOpen={deleteKeyPersonModalVisibility.visible}
        onClose={deleteKeyPersonModalVisibility.hide}
        personId={+keyPersonId}
        businessEntityId={+id}
        onSuccess={() => {
          deleteKeyPersonModalVisibility.hide();
          router.push(deleteRedirectUrl || `/corporation/${id}/key-persons`);
        }}
      />
      {(() => {
        const actionButtons =
          !disabled && isEditable ? (
            <div className="flex items-center gap-xl">
              <Button
                size="sm"
                variant="secondary"
                onClick={addKeyPersonModalVisibility.show}
              >
                Edit
              </Button>
              <Button
                size="sm"
                variant="destructive-secondary"
                onClick={deleteKeyPersonModalVisibility.show}
              >
                Delete
              </Button>
            </div>
          ) : null;

        return renderHeader ? (
          renderHeader(actionButtons)
        ) : (
          <div className="flex justify-between">
            <h3 className="display-xs-bold">{keyPersonName}</h3>
            {actionButtons}
          </div>
        );
      })()}
      <div className="flex flex-col border border-border-primary rounded-md overflow-clip divide-y divide-border-primary">
        <DataPoint
          title="Email"
          value={
            person.email ? (
              person.email
            ) : (
              <Badge size="sm" variant="warning">
                Info missing
              </Badge>
            )
          }
          onCopy={() => onCopy(person.email)}
        />
        <DataPoint
          title="Phone number"
          value={
            person.phoneNumber ? (
              person.phoneNumber
            ) : (
              <Badge size="sm" variant="warning">
                Info missing
              </Badge>
            )
          }
          onCopy={() => onCopy(person.phoneNumber)}
        />
        <DataPoint
          title="Address"
          value={
            getPrintableBusinessLocation(keyPersonAddress) ? (
              getPrintableBusinessLocation(keyPersonAddress)
            ) : (
              <Badge size="sm" variant="warning">
                Info missing
              </Badge>
            )
          }
          onCopy={() => onCopy(getPrintableBusinessLocation(keyPersonAddress))}
        />
        <DataPoint
          title="Roles"
          value={
            rolesString || (
              <Badge size="sm" variant="warning">
                Info missing
              </Badge>
            )
          }
          onCopy={() => onCopy(rolesString)}
        />
        <DataPoint
          title="Designation"
          value={
            designationString || (
              <Badge size="sm" variant="warning">
                Info missing
              </Badge>
            )
          }
          onCopy={() => onCopy(designationString)}
        />
        <DataPoint
          title="Is authorized signatory"
          value={isAuthorizedSignatory ? 'Yes' : 'No'}
          onCopy={() => onCopy(isAuthorizedSignatory ? 'Yes' : 'No')}
        />
        <DataPoint
          title="Country of Citizenship"
          value={
            person.nationalities && person.nationalities.length > 0 ? (
              CountryInfo.getCountryByCode(person.nationalities[0])?.name ||
              person.nationalities[0]
            ) : (
              <Badge size="sm" variant="warning">
                Info missing
              </Badge>
            )
          }
          onCopy={() =>
            onCopy(
              person.nationalities && person.nationalities.length > 0
                ? CountryInfo.getCountryByCode(person.nationalities[0])?.name ||
                person.nationalities[0]
                : ''
            )
          }
        />
        <DataPoint
          title="Start Date of Employment"
          value={
            person.employmentStartDate || person.startDateOfEmployment ? (
              person.employmentStartDate || person.startDateOfEmployment
            ) : (
              <Badge size="sm" variant="warning">
                Info missing
              </Badge>
            )
          }
          onCopy={() =>
            onCopy(
              person.employmentStartDate || person.startDateOfEmployment || ''
            )
          }
        />
        <DataPoint
          title="SSN/ITIN"
          type={ssnKyc?.decryptedValue ? 'password' : 'text'}
          value={
            !ssnKyc ? (
              <Badge size="sm" variant="warning">
                Info missing
              </Badge>
            ) : ssnKyc.doesNotHaveKYCId ? (
              'Not provided'
            ) : ssnKyc.decryptedValue ? (
              ssnKyc.decryptedValue
            ) : (
              `***-**-${ssnKyc.value?.slice(-4) || '****'}`
            )
          }
          onCopy={() => {
            if (ssnKyc?.decryptedValue) {
              onCopy(ssnKyc.decryptedValue);
            }
          }}
        />
        <DataPoint
          title="Director Identification Number (DIN)"
          value={person.directorIdentificationNumber || '-'}
          onCopy={() => onCopy(person.directorIdentificationNumber ?? '')}
        />
        <DataPoint
          title="Resident director"
          value={
            person.isResidentDirector === undefined ||
              person.isResidentDirector === null
              ? '-'
              : person.isResidentDirector
                ? 'Yes'
                : 'No'
          }
          onCopy={() =>
            onCopy(
              person.isResidentDirector === undefined ||
                person.isResidentDirector === null
                ? ''
                : person.isResidentDirector
                  ? 'Yes'
                  : 'No'
            )
          }
        />
      </div>
    </div>
  );
};

export { KeyPersonDetails };
